/**
 * Frontend types mirroring the backend billing DTOs.
 * Source: src/Core/Beetegre.Shared/DTOs/Tenant/Billing/BillingDtos.cs
 *       + src/Core/Beetegre.Shared/DTOs/Admin/AdminTenantBillingDto.cs
 *
 * PCI SAQ-A: Raw card data (PAN/CVV/expiry) is never collected by this SPA.
 * SetupPaymentMethodRequest/Response types have been removed.
 * Payment method management (add/remove/default) is handled via the payment
 * provider's hosted fields — no card data enters this frontend.
 */

// ── Read DTOs ─────────────────────────────────────────────────────────────────

export interface BillingOverviewResponse {
  enforced: boolean;
  subscriptions: SubscriptionDto[];
  credits: CreditBalanceDto[];
  defaultPaymentMethod: PaymentMethodDto | null;
}

export interface SubscriptionDto {
  id: string;
  ownerCode: string;
  status: SubscriptionStatus;
  currentPeriodStart: string; // ISO UTC
  currentPeriodEnd: string;   // ISO UTC
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  dunningUntil: string | null;
  createdAt: string;
}

export type SubscriptionStatus = 'Trial' | 'Active' | 'PastDue' | 'Cancelled' | 'Expired';

export interface CreditBalanceDto {
  id: string;
  appCode: string;
  remainingUnits: number;
  currency: string | null;
  lastTopUpAt: string | null;
  createdAt: string;
}

/** Display-safe payment method — no token, no raw card data. */
export interface PaymentMethodDto {
  id: string;
  provider: string;
  last4: string;
  brand: string;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  status: PaymentMethodStatus;
  createdAt: string;
}

export type PaymentMethodStatus = 'Active' | 'Cancelled' | 'Failed';

/** Wire values of the backend `PaymentTransactionStatus` enum (serialized with `ToString()`). */
export type PaymentTransactionStatus =
  | 'Pending'
  | 'Succeeded'
  | 'Failed'
  | 'RequiresAction'
  | 'Reconciled';

/** PAN/token/RedirectUrl intentionally excluded — PCI/PII. */
export interface PaymentTransactionDto {
  id: string;
  subscriptionId: string;
  provider: string;
  orderRef: string;
  periodStart: string;
  periodEnd: string;
  amount: number;
  currency: string;
  status: PaymentTransactionStatus;
  providerRef: string | null;
  attemptNumber: number;
  lastError: string | null;
  completedAt: string | null;
  createdAt: string;
  /** App the transaction credited; `null` for non-credit rows. */
  appCode: string | null;
  /** Units added by the transaction (admin grants carry `amount = 0` and only this); `null` for non-credit rows. */
  creditUnits: number | null;
}

/**
 * `PaymentTransactionDto.provider` of a SuperAdmin credit grant (no money moved, `amount = 0`,
 * `status = "Reconciled"`). Mirrors the backend provider constant; the UI renders such rows as an
 * "Admin grant" label plus the granted units instead of a zero amount.
 */
export const ADMIN_GRANT_PROVIDER = 'ADMIN_GRANT';

export interface PaymentTransactionPagedResponse {
  items: PaymentTransactionDto[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Package bundles (AB#1240) ─────────────────────────────────────────────────

/** GET /api/v1/tenants/{tenantId}/billing/packages */
export interface PackageDto {
  code: string;
  name: string;
  appCodes: string[];
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  currency: string | null;
}

// ── Manage DTOs ───────────────────────────────────────────────────────────────

export interface TopUpCreditRequest {
  units: number;
  packageCode: string | null;
  idempotencyKey: string;
}

export interface TopUpCreditResponse {
  creditBalanceId: string;
  appCode: string;
  remainingUnits: number;
  chargeRequired: boolean;
  paymentTransactionId: string | null;
  /** Present when the provider requires a hosted-payment redirect. Navigation only — never render in DOM. */
  redirectUrl?: string | null;
}

/** PUT /tenants/{tenantId}/billing/phone — AB#2193. 204 on success. */
export interface UpdateBillingPhoneRequest {
  phone: string;
}

export interface CancelSubscriptionRequest {
  cancelAtPeriodEnd: boolean;
}

// ── Subscription purchase (AB#1237) ──────────────────────────────────────────

/**
 * Wire format for BillingPeriodDto enum.
 * Backend uses [JsonConverter(typeof(JsonStringEnumConverter))] so values are strings.
 */
export type BillingPeriod = 'Monthly' | 'Yearly';

/** Request body for POST /tenants/{id}/billing/subscriptions/purchase. */
export interface SubscriptionPurchaseRequest {
  /** Package.Code (Plan / AddOn / Bundle) to subscribe to. */
  ownerCode: string;
  /** Billing cycle: "Monthly" or "Yearly" — serialized as string by the backend enum converter. */
  billingPeriod: BillingPeriod;
  /** Caller-supplied idempotency key (UUID string recommended). Required. */
  idempotencyKey: string;
}

/**
 * Response for POST /tenants/{id}/billing/subscriptions/purchase.
 * ENFORCED path: chargeRequired=true + redirectUrl present → redirect browser.
 * DORMANT path: chargeRequired=false, subscriptionId set → show success directly.
 */
export interface SubscriptionPurchaseResponse {
  /** Subscription id (DORMANT path only — created immediately). Null in ENFORCED path. */
  subscriptionId: string | null;
  /** True when a hosted payment redirect is required (ENFORCED mode). */
  chargeRequired: boolean;
  /**
   * Hosted payment page URL (ENFORCED mode only).
   * Navigation only — NEVER render in DOM; contains provider session tokens.
   */
  redirectUrl?: string | null;
}

// ── Admin DTO ─────────────────────────────────────────────────────────────────

/** An app that bills by credit (`UsesCredit = true`); a grant target whether or not a balance exists yet. */
export interface AdminCreditAppDto {
  appCode: string;
  name: string;
}

export interface AdminTenantBillingResponse {
  tenantId: string;
  enforced: boolean;
  subscriptions: SubscriptionDto[];
  credits: CreditBalanceDto[];
  /** Every credit-using app, so an app with no balance row yet can still be granted credits. */
  creditApps: AdminCreditAppDto[];
  paymentMethods: PaymentMethodDto[];
  recentTransactions: PaymentTransactionDto[];
}

/** Bounds enforced by `POST /admin/tenants/{tenantId}/billing/credits/{appCode}/grant`. */
export const ADMIN_CREDIT_GRANT_UNITS_MIN = 1;
export const ADMIN_CREDIT_GRANT_UNITS_MAX = 100_000;
export const ADMIN_CREDIT_GRANT_REASON_MIN_LENGTH = 3;
export const ADMIN_CREDIT_GRANT_REASON_MAX_LENGTH = 500;

/** Body of `POST /api/v1/admin/tenants/{tenantId}/billing/credits/{appCode}/grant` (SuperAdminOnly). */
export interface GrantCreditRequest {
  units: number;
  /** Audit justification; the server trims it and bounds it to 3..500 characters. */
  reason: string;
  /** Stable across retries of one submission so a double-submit cannot grant twice. */
  idempotencyKey: string;
}

export interface GrantCreditResponse {
  creditBalanceId: string;
  appCode: string;
  remainingUnits: number;
  paymentTransactionId: string;
  /** True when the idempotency key had already been applied and the stored result was returned. */
  replayed: boolean;
}

// ── Billing access gate (docs/design/billing-access-gate.md, 2026-09-11) ──────

export type BillingAccessState = 'Entitled' | 'Blocked';

/**
 * `message` on the 402 ProblemDetails body `TenantAccessGateMiddleware` returns for a Blocked tenant.
 * Compared against by the shared axios interceptor (lib/api.ts) — CLAUDE.md magic-string rule.
 */
export const BILLING_ACCESS_BLOCKED_ERROR_KEY = 'billing.access.blocked';

// `BILLING_PHONE_REQUIRED_ERROR_KEY` moved to `types/apiErrorKeys.ts` as `BILLING_PHONE_ERROR_KEYS`
// (the TS<->C# parity registry `ApiErrorKeysParityTests` reads) — import from there instead.

/**
 * Mirrors `Beetegre.Application.Billing.TenantAccessReasons` (src/Core/Beetegre.Application/
 * Billing/ITenantAccessService.cs) — CLAUDE.md magic-string rule. Low-cardinality reason for the
 * current `BillingAccessState`, safe to branch on in the UI without stringly-typed comparisons.
 */
export type BillingAccessReason =
  | 'not_enforced'
  | 'demo'
  | 'transition'
  | 'subscription'
  | 'credit'
  | 'none'
  | 'failed_open';

export const BILLING_ACCESS_REASONS = {
  NotEnforced: 'not_enforced',
  Demo: 'demo',
  Transition: 'transition',
  Subscription: 'subscription',
  Credit: 'credit',
  None: 'none',
  FailedOpen: 'failed_open',
} as const satisfies Record<string, BillingAccessReason>;

/**
 * GET /api/v1/tenants/{tenantId}/billing/access — also carried on the login /
 * switch-tenant envelope as `billing`. Null for SuperAdmin / a tenant-less
 * user (contract doc "Frontend" section).
 */
export interface BillingAccessSummary {
  enforced: boolean;
  accessState: BillingAccessState;
  reason: BillingAccessReason;
  accessEndsAt: string | null;
}

// ── Platform billing settings (SuperAdmin demo control) ───────────────────────

/** GET/PUT /api/v1/admin/billing/settings (SuperAdminOnly). */
export interface PlatformBillingSettingsResponse {
  demoEnabled: boolean;
  demoDays: number;
  updatedAt: string;
}

export interface UpdatePlatformBillingSettingsRequest {
  demoEnabled: boolean;
  demoDays: number;
}
